import Fuse from 'fuse.js'
// import kuromoji from 'kuromoji' // 辞書ロード版で使用する場合はコメント解除

// ========================================
// 検索モード設定
// ========================================
// true: 軽量版（ひらがな・カタカナのみ正規化、辞書ロードなし）
// false: 辞書ロード版（漢字→ひらがな変換あり、18MB辞書ロード、初回1-2秒）
const USE_LIGHTWEIGHT_MODE = true

// ========================================
// 辞書ロード版に戻す手順:
// ========================================
// 1. 上記の USE_LIGHTWEIGHT_MODE を false に変更
// 2. 1行目の kuromoji import のコメントを解除
// 3. 下記の「辞書ロード版のコード」セクションのコメントを解除
// 4. normalizeText 関数内の分岐を辞書ロード版に切り替え
// ========================================

// ひらがなをカタカナに変換
const toKatakana = (str) => {
  return str.replace(/[\u3041-\u3096]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0x60))
}

// ========================================
// 辞書ロード版のコード（現在無効化）
// ========================================
// 以下のコメントを解除すると辞書ロード版として動作します
// ========================================
/*
// kuromojiのトークナイザーをキャッシュ
let tokenizerPromise = null

// kuromojiの初期化
const getTokenizer = () => {
  if (!tokenizerPromise) {
    tokenizerPromise = new Promise((resolve, reject) => {
      kuromoji.builder({ dicPath: '/dict' }).build((err, tokenizer) => {
        if (err) {
          reject(err)
        } else {
          resolve(tokenizer)
        }
      })
    })
  }
  return tokenizerPromise
}

// 漢字をひらがなに変換（kuromoji使用）
const kanjiToHiragana = async (text) => {
  try {
    const tokenizer = await getTokenizer()
    const tokens = tokenizer.tokenize(text)
    return tokens.map(token => token.reading || token.surface_form).join('')
  } catch (err) {
    console.warn('kuromoji conversion failed:', err)
    return text
  }
}
*/
// ========================================
// 辞書ロード版のコード ここまで
// ========================================

// HTMLタグを除去してカタカナに正規化
const normalizeText = async (text) => {
  const withoutTags = text.replace(/<[^>]*>/g, '')

  if (USE_LIGHTWEIGHT_MODE) {
    // 軽量版: ひらがな→カタカナのみ
    // 「とうきょう」で「東京都」はヒットしない
    // 「トウキョウト」や「東京都（トウキョウト）」のような表記があればヒット
    return toKatakana(withoutTags)
  } else {
    // 辞書ロード版: 漢字→ひらがな→カタカナ
    // 「とうきょう」で「東京都」がヒット（要kuromoji）
    // 以下の行のコメントを解除してください:
    // const hiragana = await kanjiToHiragana(withoutTags)
    // return toKatakana(hiragana)

    // 現在は軽量版として動作（フォールバック）
    return toKatakana(withoutTags)
  }
}

const searchNodes = async (nodes, searchText) => {
  if (!searchText || searchText.trim() === '') {
    return nodes
  }

  // すべてのノードをフラット化（パス情報を保持、非同期版）
  const flattenNodes = async (nodes, parentPath = []) => {
    const result = []

    for (const node of nodes) {
      const currentPath = [...parentPath, node]
      // 正規化されたlabelを追加（非同期）
      const normalizedLabel = await normalizeText(node.label || '')
      result.push({
        node,
        path: currentPath,
        normalizedLabel,
      })

      if (Array.isArray(node.nodes) && node.nodes.length > 0) {
        const childResults = await flattenNodes(node.nodes, currentPath)
        result.push(...childResults)
      }
    }

    return result
  }

  // Fuse.jsの設定（バランス重視）
  const fuseOptions = {
    keys: [
      'node.label', // 元のlabelでも検索
      'normalizedLabel', // 正規化されたlabelでも検索
    ],
    threshold: 0.4, // 適度な曖昧さを許容（0.0=完全一致 ~ 1.0=何でも一致）
    distance: 100, // マッチ位置の許容範囲
    minMatchCharLength: 1, // 1文字から検索可能
    ignoreLocation: true, // 文字列のどこにあってもマッチ
    includeScore: true, // スコアを含める（将来的にソート可能）
  }

  // フラット化したノードでFuse検索
  const flatNodes = await flattenNodes(nodes)
  const fuse = new Fuse(flatNodes, fuseOptions)
  // 検索文字も正規化（漢字→ひらがな→カタカナ）
  const normalizedSearchText = await normalizeText(searchText)
  const searchResults = fuse.search(normalizedSearchText)

  // マッチしたノードのパス情報を取得
  const matchedPaths = searchResults.map((result) => result.item.path)

  // マッチしたノードとその親を含むツリーを再構築
  const getNodes = (result, node) => {
    // このノード自身がマッチしているか
    const isDirectMatch = matchedPaths.some((path) => path[path.length - 1].id === node.id)

    // 子孫がマッチしているか（親フォルダも表示するため）
    const hasMatchedDescendant = matchedPaths.some((path) => path.some((pathNode) => pathNode.id === node.id))

    if (isDirectMatch) {
      result.push(node)
      return result
    }

    if (hasMatchedDescendant && Array.isArray(node.nodes)) {
      const childNodes = node.nodes.reduce(getNodes, [])
      if (childNodes.length) {
        result.push({ ...node, nodes: childNodes })
      }
    }

    return result
  }

  return nodes.reduce(getNodes, [])
}

export default searchNodes
